import { useEffect, useMemo, useState } from 'react';
import { createApiClient } from '../lib/api-client.js';
import type { UsageMonthlyResponse } from '../lib/types.js';

export interface TokenUsageBadgeProps {
  /** 可选注入 client（测试用） */
  api?: ReturnType<typeof createApiClient>;
  /** 刷新间隔 ms，默认 30s；传 0 关闭轮询 */
  intervalMs?: number;
}

/**
 * 顶 nav 月度 token 用量徽章 — 只对 admin 可见（非 admin 时自动隐藏）。
 * 显示：当月 in/out token 累计 · N 次编辑。全站不展示 $ 费用概念，只看绝对 token 值。
 */
function fmtTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}
export default function TokenUsageBadge({ api, intervalMs = 30_000 }: TokenUsageBadgeProps) {
  const [isAdmin, setIsAdmin] = useState(false);
  const [usage, setUsage] = useState<UsageMonthlyResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // ⚠️ 必须 useMemo——否则每次 re-render 新建 client，下面两个 useEffect 的 [client]
  // dep 永远变，effect 不停 re-run → me() / getMonthlyUsage() 死循环刷接口（实际事故见
  // server 日志 [GET] /api/me 200 / [GET] /api/admin/usage/monthly 200 每秒数十次）。
  const client = useMemo(() => api ?? createApiClient(), [api]);

  useEffect(() => {
    client
      .me()
      .then((res) => {
        if (res.user && res.user.is_admin === true) setIsAdmin(true);
      })
      .catch(() => {
        /* anonymous → 隐藏 */
      });
  }, [client]);

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    async function load() {
      try {
        const res = await client.getMonthlyUsage();
        if (!cancelled) {
          setUsage(res);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'failed');
      }
    }
    load();
    if (intervalMs > 0) {
      const t = setInterval(load, intervalMs);
      return () => {
        cancelled = true;
        clearInterval(t);
      };
    }
    return () => {
      cancelled = true;
    };
  }, [isAdmin, intervalMs, client]);

  if (!isAdmin) return null;
  if (error) {
    return (
      <span
        className="hidden whitespace-nowrap rounded-full bg-rose-50 px-2 py-0.5 text-xs text-rose-600 md:inline"
        data-testid="token-usage-badge-error"
        title={error}
      >
        usage: 加载失败
      </span>
    );
  }
  if (!usage) {
    return (
      <span
        className="hidden whitespace-nowrap rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500 md:inline"
        data-testid="token-usage-badge-loading"
      >
        usage: …
      </span>
    );
  }
  const inTok = usage.totals.input_tokens;
  const outTok = usage.totals.output_tokens;
  return (
    <span
      className="hidden whitespace-nowrap rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700 md:inline"
      data-testid="token-usage-badge"
      title={`本月 ${usage.month}：${usage.totals.edits_count} 次编辑 · in ${inTok} / out ${outTok}`}
    >
      {usage.month} · in {fmtTokens(inTok)} · out {fmtTokens(outTok)} · {usage.totals.edits_count}{' '}
      次
    </span>
  );
}
